/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  EditorState, RichUtils, convertToRaw,
} from 'draft-js';
import Editor from '@draft-js-plugins/editor';
import 'draft-js/dist/Draft.css';
import { withRouter } from 'react-router-dom';
import BookLink from '../book-components/book-link';

import { createPage, updatePage, fetchPage } from '../../actions';
import plugins from './plugins';
import MediaPicker from './media-picker';
import loadPage from './load-page';

// Component uses Facebook's Draft.JS to allow for all sorts of rich-text.

// plugins function creates 1 set of plugins that can be used in any editor.
const plug = plugins();

const Style = {
  BOLD: 'BOLD',
  ITALIC: 'ITALIC',
  UNDERLINE: 'UNDERLINE',
  HIGHLIGHT: 'HIGHLIGHT',
};

const Type = {
  H2: 'header-two',
  H3: 'header-three',
  H4: 'header-four',
  UL: 'unordered-list-item',
  OL: 'ordered-list-item',
  QUOTE: 'blockquote',
};

function NewPage(props) {
  const bId = props.match.params.bookID;
  const chId = props.match.params.chapterID;
  const pId = props.match.params.pageID;

  const [pickingImg, setPickingImg] = useState(false);
  const [pickingVid, setPickingVid] = useState(false);
  const [pickingEmbed, setPickingEmbed] = useState(false);

  const [title, setTitle] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [editorState, setEditorState] = useState(
    () => EditorState.createEmpty(),
  );
  const editor = useRef(null);

  // load page state, if in edit mode
  useEffect(() => {
    if (pId) {
      props.fetchPage(bId, chId, pId);
    }
    editor.current.focus();
  }, []);

  // when fetch page returns, import into editor
  useEffect(() => {
    if (props.page.id === pId && props.page.content) {
      setTitle(props.page.title);
      setEditorState(loadPage(props.page.content));
    }
  }, [props.page]);

  const onStyleClick = (style) => {
    setEditorState(RichUtils.toggleInlineStyle(editorState, style));
  };

  const onTypeClick = (type) => {
    setEditorState(RichUtils.toggleBlockType(editorState, type));
  };

  const addImage = (url, width) => {
    setEditorState(plug.imagePlugin.addImage(editorState, url, width));
  };

  const addVid = (url, width) => {
    setEditorState(plug.vidPlugin.addVideo(editorState, url, width));
  };

  const addPDF = (url, width) => {
    setEditorState(plug.pdfPlugin.addPDF(editorState, url, width));
  };

  const closeModal = () => {
    setPickingVid(false);
    setPickingImg(false);
    setPickingEmbed(false);
  };

  const modalPicker = () => {
    if (pickingImg) return <MediaPicker addMedia={addImage} closeModal={closeModal} />;
    if (pickingVid) return <MediaPicker addMedia={addVid} closeModal={closeModal} />;
    if (pickingEmbed) return <MediaPicker addMedia={addPDF} closeModal={closeModal} />;
    else return <></>;
  };

  const savePage = () => {
    if (!title) {
      setErrorMsg('Page title must be non-empty!');
      return;
    }
    const raw = convertToRaw(editorState.getCurrentContent());
    const plainTxt = editorState.getCurrentContent().getPlainText();
    if (!plainTxt) {
      setErrorMsg('Need some text on the page!');
      return;
    }
    const page = { title, content: JSON.stringify(raw), plainTxt };
    if (pId) props.updatePage(bId, chId, pId, page, props.history);
    else props.createPage(bId, chId, page, props.history);
  };

  const [showStyleTool, setShowStyleTool] = useState(false);
  const [showTypeTool, setShowTypeTool] = useState(false);
  const [showMediaTool, setShowMediaTool] = useState(false);

  // Only show 1 toolbar @ time on mobile.
  const setTool = (tool) => {
    switch (tool) {
      case 'STYLE':
        setShowStyleTool(!showStyleTool);
        setShowTypeTool(false);
        setShowMediaTool(false);
        break;
      case 'TYPE':
        setShowStyleTool(false);
        setShowTypeTool(!showTypeTool);
        setShowMediaTool(false);
        break;
      case 'MEDIA':
        setShowStyleTool(false);
        setShowTypeTool(false);
        setShowMediaTool(!showMediaTool);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <BookLink className="chapter-title-header" to={`/books/${bId}/chapters/${chId}/pages`}>
        <p>{props?.currentChapter?.title}</p>
      </BookLink>

      <div className="story-container">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="type title..." className="title-input" />
        <div className="editor">
          <div className="toolbars">
            <div className="buttons">
              <button onClick={() => setTool('STYLE')} type="button" className={showStyleTool ? 'style-active' : ''}>Style</button>
              <button onClick={() => setTool('TYPE')} type="button" className={showTypeTool ? 'type-active' : ''}>H2+</button>
              <button onClick={() => setTool('MEDIA')} type="button" className={showMediaTool ? 'media-active' : ''}>Media</button>
            </div>
            <div className={showStyleTool ? 'show style-toolbar' : 'style-toolbar'}>
              <button onClick={() => { onStyleClick(Style.ITALIC); }} type="button" aria-label="italicize"><i className="fas fa-italic" /></button>
              <button onClick={() => { onStyleClick(Style.BOLD); }} type="button" aria-label="embolden"><i className="fas fa-bold" /></button>
              <button onClick={() => { onStyleClick(Style.UNDERLINE); }} type="button" aria-label="underline"><i className="fas fa-underline" /></button>
              <button onClick={() => { onStyleClick(Style.HIGHLIGHT); }} type="button" aria-label="highlight"><i className="fas fa-highlighter" /></button>
            </div>
            <div className={showTypeTool ? 'show block-type-toolbar' : 'block-type-toolbar'}>
              <button onClick={() => { onTypeClick(Type.P); }} type="button" aria-label="make paragraph"><i className="fas fa-paragraph" /></button>
              <button onClick={() => { onTypeClick(Type.H2); }} type="button" aria-label="make H2">H2</button>
              <button onClick={() => { onTypeClick(Type.H3); }} type="button" aria-label="make H3">H3</button>
              <button onClick={() => { onTypeClick(Type.H4); }} type="button" aria-label="make H4">H4</button>
              <button onClick={() => { onTypeClick(Type.UL); }} type="button" aria-label="make unordered list"><i className="fas fa-list-ul" /></button>
              <button onClick={() => { onTypeClick(Type.OL); }} type="button" aria-label="make ordered list"><i className="fas fa-list-ol" /></button>
              <button onClick={() => { onTypeClick(Type.QUOTE); }} type="button" aria-label="make quote"><i className="fas fa-quote-right" /></button>
            </div>
            <div className={showMediaTool ? 'show media-toolbar' : 'media-toolbar'}>
              <button onClick={() => { setPickingImg(true); }} type="button" aria-label="add image"><i className="fas fa-image" /></button>
              <button onClick={() => { setPickingVid(true); }} type="button" aria-label="add video"><i className="far fa-file-video" /></button>
              <button onClick={() => { setPickingEmbed(true); }} type="button" aria-label="add pdf"><i className="fas fa-file-alt" /></button>
            </div>
          </div>
          <Editor editorState={editorState}
            onChange={setEditorState}
            plugins={plug.plugins}
            ref={editor}
            placeholder="page content here..."
          />
          <button onClick={() => savePage()} type="button" aria-label="Save Page"><i className="fas fa-save" /></button>
          <p>{errorMsg}</p>
        </div>
        {modalPicker()}
      </div>
    </>
  );
}

const mapStateToProps = (state) => (
  {
    currentBook: state.books.current,
    currentChapter: state.chapters.current,
    page: state.pages.current,
  }
);

export default withRouter(connect(mapStateToProps, { createPage, updatePage, fetchPage })(NewPage));
